import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Chip } from '@mui/material';
import { isToday, isTomorrow, isSameDay, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getTimeFormat } from '@utils/dateHelper';
import { getStatusMeta } from './statusMeta';
import StatusChip from './StatusChip';
import type { Reservation } from '@app-types/models/reservation';

const planName = (r: Reservation, lang: string): string =>
  (r?.planId?.[lang === 'zh' ? 'zh_name' : 'name'] as string) || r?.planId?.name || '';

interface AgendaFilter {
  key: string;
  match: (r: Reservation, todayStart: Date) => boolean;
}

// Note: cancelled reservations are excluded server-side, so there's no "cancelled" filter.
const FILTERS: AgendaFilter[] = [
  { key: 'upcoming', match: (r, todayStart) => new Date(r.start) >= todayStart && (r.status === 'confirmed' || r.status === 'pending-for-approval') },
  { key: 'completed', match: (r) => r.status === 'completed' || r.status === 'auto-completed' },
  { key: 'noshow', match: (r) => r.status === 'no-show' || r.status === 'approval-timeout' },
];

interface AgendaViewProps {
  reservations: Reservation[];
  onSelectBooking: (r: Reservation) => void;
}

// Bookings grouped by day, as tappable cards, filterable by Upcoming / Completed / No-show.
const AgendaView: React.FC<AgendaViewProps> = ({ reservations, onSelectBooking }) => {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState('upcoming');

  const counts = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const c: Record<string, number> = {};
    FILTERS.forEach((f) => { c[f.key] = reservations.filter((r) => f.match(r, todayStart)).length; });
    return c;
  }, [reservations]);

  // Filtered + sorted (upcoming: soonest first; history: most recent first), grouped by calendar day.
  const groups = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const def = FILTERS.find((f) => f.key === filter)!;
    const asc = filter === 'upcoming';
    const list = reservations
      .filter((r) => def.match(r, todayStart))
      .sort((a, b) => (asc
        ? new Date(a.start).getTime() - new Date(b.start).getTime()
        : new Date(b.start).getTime() - new Date(a.start).getTime()));

    const out: Array<{ date: Date; items: Reservation[] }> = [];
    list.forEach((r) => {
      const d = new Date(r.start);
      let group = out.find((g) => isSameDay(g.date, d));
      if (!group) { group = { date: d, items: [] }; out.push(group); }
      group.items.push(r);
    });
    return out;
  }, [reservations, filter]);

  const dayLabel = (date: Date): string => {
    if (isToday(date)) return `${t('reservationsV2.today')} · ${format(date, 'EEE d MMM')}`;
    if (isTomorrow(date)) return `${t('reservationsV2.tomorrow')} · ${format(date, 'EEE d MMM')}`;
    return format(date, 'EEE d MMM');
  };

  const chips = (
    <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1.5, flexWrap: 'wrap', gap: 1 }}>
      {FILTERS.map((f) => (
        <Chip
          key={f.key}
          label={`${t(`reservationsV2.filter.${f.key}`)}${counts[f.key] ? ` (${counts[f.key]})` : ''}`}
          size="small"
          onClick={() => setFilter(f.key)}
          variant={filter === f.key ? 'filled' : 'outlined'}
          color={filter === f.key ? 'primary' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      ))}
    </Stack>
  );

  if (groups.length === 0) {
    return (
      <Box>
        {chips}
        <Typography color="text.secondary" fontSize={13} sx={{ textAlign: 'center', py: 5 }}>
          {t('reservationsV2.noBookings')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      {chips}
      {groups.map((g) => (
        <Box key={g.date.toISOString()} sx={{ pt: 1.5, px: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1.25, mb: 1, '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' } }}>
            {dayLabel(g.date)}
          </Typography>

          {g.items.map((r) => {
            const meta = getStatusMeta(r.status);
            return (
              <Stack
                key={r._id}
                direction="row"
                spacing={1.5}
                alignItems="stretch"
                onClick={() => onSelectBooking(r)}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1, cursor: 'pointer', '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }, transition: 'box-shadow .15s, transform .15s' }}
              >
                <Box sx={{ width: 4, borderRadius: 1, bgcolor: meta.color, flex: 'none' }} />
                <Box sx={{ minWidth: 92 }}>
                  <Typography fontWeight={700} fontSize={14}>{getTimeFormat(r.start)}</Typography>
                  <Typography fontSize={12} color="text.secondary">{getTimeFormat(r.end)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={700} fontSize={14}>{r.customerName}</Typography>
                  <Typography fontSize={13} color="text.secondary">
                    {planName(r, i18n.language)}{r.discountedAmount != null ? ` · $${r.discountedAmount}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ alignSelf: 'center' }}><StatusChip status={r.status} /></Box>
              </Stack>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default AgendaView;
